/**
 * Landing footer. StampmatS logo + one-line description, three link columns,
 * and the Aigentico attribution (badge + wordmark, linked) kept subtle and
 * footer-only.
 *
 * Privacy, Terms and Contact do not exist as routes yet and the router has no
 * placeholder pattern (unknown paths redirect to "/"), so they are rendered as
 * plain, clearly labelled "in preparation" text rather than as links.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../Logo";
import { AigenticoBadge } from "../AigenticoBadge";
import { NAV_ITEMS } from "./content";

const LEGAL_PENDING = ["Privacy", "Terms", "Contact"] as const;

export function LandingFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-border bg-white">
      <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr] md:gap-8">
          <div>
            <Link to="/" className="inline-flex min-h-[44px] items-center rounded-md" aria-label="StampmatS home">
              <Logo className="h-9" />
            </Link>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-ink-soft">
              StampmatS turns digital files into verifiable evidence.
            </p>
          </div>

          <FooterColumn title="Product">
            {NAV_ITEMS.map((item) => (
              <li key={item.href}>
                <a href={item.href} className="inline-flex min-h-[44px] items-center text-sm text-ink-soft hover:text-aigentico-navy">
                  {item.label}
                </a>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title="Account">
            <li>
              <Link to="/signin" className="inline-flex min-h-[44px] items-center text-sm text-ink-soft hover:text-aigentico-navy">
                Sign in
              </Link>
            </li>
            <li>
              <Link to="/signup" className="inline-flex min-h-[44px] items-center text-sm text-ink-soft hover:text-aigentico-navy">
                Create account
              </Link>
            </li>
          </FooterColumn>

          <FooterColumn title="Legal" nav={false}>
            {LEGAL_PENDING.map((label) => (
              <li key={label} className="flex min-h-[44px] items-center text-sm text-ink-faint">
                {label}
                <span className="ml-2 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">
                  in preparation
                </span>
              </li>
            ))}
          </FooterColumn>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-border pt-6 text-xs text-ink-faint sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} StampmatS. All rights reserved.</p>
          <a
            href="https://aigentico.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center gap-2 self-start text-ink-soft transition-colors hover:text-aigentico-navy sm:self-auto"
          >
            <AigenticoBadge size={22} />
            <span>
              A product of <span className="font-medium text-aigentico-navy">Aigentico</span>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children, nav = true }: { title: string; children: ReactNode; nav?: boolean }) {
  const heading = <h2 className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-ink">{title}</h2>;
  const list = <ul className="mt-3 space-y-0.5">{children}</ul>;
  return nav ? (
    <nav aria-label={title}>
      {heading}
      {list}
    </nav>
  ) : (
    <div>
      {heading}
      {list}
    </div>
  );
}
